import {Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import Box from '@mui/material/Box';
import type * as React from 'react';
import {useMemo} from 'react';
import shortMonths from '@/constants/shortMonths';
import buildSubDisplayDateConfig from '@/functions/buildSubDisplayDateConfig';
import type {CapacityConfig} from '@/types/CapacityConfig';

const buildCapacityCells = (capacityYear : number, capacityMonth : number) => {
    const cells = [];
    let currentCapacityYear = capacityYear;
    let currentCapacityMonth = capacityMonth;

    for (let i = 0; i < 7; i++) {
        cells.push(`${currentCapacityYear}-${currentCapacityMonth.toString().padStart(2, '0')}`);

        currentCapacityMonth = currentCapacityMonth === 12 ? 1 : currentCapacityMonth + 1;

        if (currentCapacityMonth === 1) {
            currentCapacityYear++;
        }
    }

    return cells;
};

type Props = {
    fiscalMonth : string;
    defaultCapacityYearMonth : string;
    capacityConfig : CapacityConfig;
    capacityTotals : Record<string, number>;
};

const EmployeeCapacityTotalsTable = (
    {
        fiscalMonth,
        capacityConfig,
        capacityTotals,
    } : Props
) : React.ReactElement => {
    const yearMonthsDisplay = useMemo(() => {
        const capacityYearMonthSplit = fiscalMonth.split('-');

        return buildCapacityCells(
            parseInt(capacityYearMonthSplit[0]),
            parseInt(capacityYearMonthSplit[1])
        );
    }, [fiscalMonth]);

    const subDisplay = buildSubDisplayDateConfig(capacityConfig, yearMonthsDisplay);

    return <TableContainer component={Paper} sx={{mb: 2}}>
        <Table size="small">
            <TableHead>
                <TableRow
                    sx={{
                        backgroundColor: 'rgb(224, 237, 244)',
                    }}
                >
                    <TableCell
                        sx={{
                            width: '25% !important',
                        }}
                    />
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        const split = capacityYearMonth.split('-');
                        const month = parseInt(split[1]) - 1;

                        return <TableCell
                            key={`header-cell-${capacityYearMonth}`}
                            sx={{
                                textAlign: 'center',
                                pr: 3,
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: 'column',
                                    width: '100%',
                                }}
                            >
                                <Box sx={{fontWeight: 800}}>{shortMonths[month]} {split[0]}</Box>
                                <Box>{subDisplay[capacityYearMonth]}</Box>
                            </Stack>
                        </TableCell>;
                    })}
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow
                    sx={{
                        backgroundColor: 'rgb(224, 237, 244)',
                    }}
                >
                    <TableCell
                        sx={{
                            width: '25% !important',
                        }}
                    >
                        Total
                    </TableCell>
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        const total = capacityTotals[capacityYearMonth];

                        return <TableCell
                            key={`top-capacity-total-totals-${capacityYearMonth}`}
                            sx={{
                                fontWeight: 800,
                                textAlign: 'center',
                                pr: 3,
                            }}
                        >
                            {isNaN(total) ? '' : Math.round(total * 10) / 10}
                        </TableCell>;
                    })}
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>;
};

export default EmployeeCapacityTotalsTable;
